/**
 * Shared generators for interview scheduling UIs (K Interview Scheduler, Schedule with AI, etc.).
 */

export interface TimeIntervalOption {
  id: number;
  label: string;
  value: string;
}

export interface DurationIntervalOption {
  label: string;
  value: number;
}

export interface GenerateTimeIntervalsParams {
  /** Use 12-hour labels (true) or 24-hour (false). Default true. */
  is12Hour?: boolean;
  /** Step in minutes. Default 15. */
  interval?: number;
}

/**
 * Full-day time slots (e.g. for start/end time pickers).
 */
export function generateTimeIntervals({
  is12Hour = true,
  interval = 15,
}: GenerateTimeIntervalsParams = {}): TimeIntervalOption[] {
  const intervals: TimeIntervalOption[] = [];
  const totalMinutes = 24 * 60;

  for (let minutes = 0; minutes < totalMinutes; minutes += interval) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    const displayHours = is12Hour
      ? hours === 0
        ? 12
        : hours > 12
          ? hours - 12
          : hours
      : hours;
    const timeStr = `${displayHours.toString().padStart(2, '0')}:${mins
      .toString()
      .padStart(2, '0')}${is12Hour ? ` ${hours < 12 ? 'AM' : 'PM'}` : ''}`;

    intervals.push({
      id: intervals.length + 1,
      label: timeStr,
      value: timeStr,
    });
  }

  return intervals;
}

export interface GenerateDurationIntervalsParams {
  /** Maximum duration in hours. Default 24. */
  maxDuration?: number;
  /** Step in minutes. Default 15. */
  interval?: number;
}

/**
 * Duration dropdown options: e.g. 15 min … 24 hours in fixed steps.
 */
export function generateDurationIntervals({
  maxDuration = 24,
  interval = 15,
}: GenerateDurationIntervalsParams = {}): DurationIntervalOption[] {
  const intervals: DurationIntervalOption[] = [];
  const maxMinutes = maxDuration * 60;

  for (let minutes = interval; minutes <= maxMinutes; minutes += interval) {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    let label: string;

    if (hours === 0) {
      label = `${mins} min`;
    } else if (mins === 0) {
      label = `${hours} hour${hours > 1 ? 's' : ''}`;
    } else {
      label = `${hours} hour${hours > 1 ? 's' : ''} ${mins} min`;
    }

    intervals.push({ label, value: minutes });
  }

  return intervals;
}
